export class LocaleString {

    [locale: string]: string;

    /**
     * Compare two LocaleStrings even if they are undefined or null
     * Will only return true if both LocaleString have the same languages
     * and same value for each language, unless using param `ignoreExtraLanguages`
     * in which case only languages in common are compared
     * @param a LocaleString A to compare with B
     * @param b LocaleString B to compare with A
     * @param ignoreExtraLanguages If set to true, it will only compare the values for languages
     * found in both A and B, defaults to false
     */
    public static equals(a: LocaleString, b: LocaleString, ignoreExtraLanguages: boolean = false): boolean {
        if (!a || !b) {
            return !a && !b;
        }

        const langs = [];
        const langsA = Object.keys(a).filter(l => Boolean(a[l]));
        const langsB = Object.keys(b).filter(l => Boolean(b[l]));

        if (langsA.length !== langsB.length) {
            // Different amount of languages for A and B
            if (!ignoreExtraLanguages) {
                // They are not equals
                return false;
            }

            // Ignoring extra languages, only compare common languages
            langs.push(...langsA.filter(l => langsB.includes(l)));
        } else {
            // Same amount of languages for both A and B
            langs.push(...langsA.filter(l => langsB.includes(l)));
            if (langs.length !== langsA.length) {
                // There were some languages not common to both A and B
                if (!ignoreExtraLanguages) {
                    // They are not equals
                    return false;
                }
            }
            // Continue only with the common languages
        }

        for (const lang of langs) {
            if (a[lang] !== b[lang]) {
                return false;
            }
        }

        return true;
    }

    /**
     * Removes languages that contain empty string, undefined or null from the localeString and returns it
     * @param localeString LocaleString to clean
     */
    public static clean(localeString: LocaleString): LocaleString {
        const cleaned = new LocaleString();
        for (const [lang, value] of Object.entries(localeString)) {
            if (Boolean(value)) {
                cleaned[lang] = value;
            }
        }
        return cleaned;
    }
}
